/* notes.ts — places numbered notes, and previews them on hover.
 *
 * Two modes, chosen by the viewport rather than by the reader:
 *   margin  the note floats into the right gutter, Tufte-style
 *   end     the notes are collected into a numbered "Notes" list at the foot of
 *           the page, with links both ways
 *
 * There is only a gutter to float into above the note-float breakpoint, so
 * margin is used whenever there is room and end whenever there is not, live on
 * resize. Notes are never hidden behind a hover: that made them unreachable by
 * touch and looked like they had simply vanished when you narrowed the window.
 *
 * This used to be a Margin | End | Off toggle persisted in localStorage. The
 * control is gone — the right answer is fully determined by the viewport, so
 * asking the reader was a decision they had no basis to make.
 *
 * Only "end" moves DOM nodes; the rest is CSS driven by data-notes on <html>.
 * Each note's original position is recorded up front so margin mode restores it
 * exactly.
 */

(function () {
    'use strict';

    /** Where a note can live. Chosen by the viewport, never by the reader. */
    type Mode = 'margin' | 'end';

    /** A note lifted out of the page into the shared popup, plus the exact
        position to put it back. */
    interface Borrowed {
        note: HTMLElement;
        slot: { parent: ParentNode | null; next: ChildNode | null };
    }

    const root = document.documentElement;
    const post = document.querySelector('.post-content') || document.getElementById('main-content');
    if (!post) {
        return;
    }

    const notes = Array.from(post.querySelectorAll<HTMLElement>('.note'));
    if (notes.length === 0) {
        return;
    }

    /* Where each note started, so margin mode can put it back exactly. */
    const home = notes.map(function (note) {
        return { node: note, parent: note.parentNode, next: note.nextSibling };
    });

    /* Marker = the superscript number that sits before the note. Resolved by id
       rather than by walking up from the note, because in end mode the note has
       been moved out of its .note-wrap and .closest() would find nothing. */
    function markerFor(note: HTMLElement) {
        return document.getElementById(note.id.replace('note-', 'noteref-'));
    }

    /* ── Build the endnotes container (empty until "end" is selected) ── */
    const aside = document.createElement('aside');
    aside.className = 'endnotes';
    aside.hidden = true;

    const heading = document.createElement('h2');
    heading.id = 'notes';
    heading.className = 'endnotes-heading';
    heading.textContent = 'Notes';

    const list = document.createElement('ol');
    list.className = 'endnotes-list';

    aside.appendChild(heading);
    aside.appendChild(list);
    post.appendChild(aside);

    /* Numbering and ids come from _includes/note.html, at build time, so the
       marker and the list at the foot of the page cannot disagree and the
       anchors work with JavaScript disabled. */
    notes.forEach(function (note) {
        const marker = markerFor(note);
        if (!marker) {
            return;
        }
        marker.setAttribute('data-note', note.id.replace('note-', ''));

        /* Preview only makes sense when the note is not already beside the
           text; in margin mode it is right there in the gutter. */
        function previewable() {
            return root.getAttribute('data-notes') === 'end';
        }

        marker.addEventListener('mouseenter', function () {
            if (previewable()) {
                showPopup(marker, note);
            }
        });
        /* :focus-visible, not plain focus. Landing on #noteref-N from a shared
           link also focuses the marker, and that should not fire a preview —
           only a reader who tabbed here wants one. */
        marker.addEventListener('focus', function () {
            if (previewable() && marker.matches(':focus-visible')) {
                showPopup(marker, note);
            }
        });
        /* Delayed, so the pointer can travel from the marker into the popup —
           note text may contain links, and an instant hide made them
           unclickable. */
        marker.addEventListener('mouseleave', scheduleHide);
        marker.addEventListener('blur', scheduleHide);

        /* The marker is a real <a href="#note-N">, which is what makes it work
           with JavaScript off. With JavaScript on we scroll it ourselves, so
           the URL does not collect a hash for every note the reader glances at.
           In margin mode there is nowhere to go — the note is in the gutter,
           level with the marker — so the click is simply swallowed. */
        marker.addEventListener('click', function (e) {
            e.preventDefault();
            if (!previewable()) {
                return;
            }
            /* Return the note to the list before scrolling to it — while the
               popup is open the note lives inside the popup, not the page. */
            hidePopup();
            /* 'instant', not the default 'auto': auto defers to CSS
               scroll-behavior, so a stylesheet could silently reintroduce the
               animation. This matches the ↩ back-link, which is a plain anchor
               and has always jumped. */
            note.scrollIntoView({ behavior: 'instant', block: 'center' });
        });
    });

    function toEnd() {
        notes.forEach(function (note) {
            const item = document.createElement('li');
            item.className = 'endnotes-item';
            item.appendChild(note);

            const n = note.id.replace('note-', '');
            const back = document.createElement('a');
            back.className = 'endnotes-back';
            back.href = '#noteref-' + n;
            back.textContent = '↩';
            back.setAttribute('aria-label', 'Back to reference ' + n);

            /* Scroll rather than let the browser follow the fragment. Native
               fragment navigation FOCUSES the target, and the marker is
               focusable (tabIndex 0) with a focus handler that opens the
               preview — so "return to the text" was popping a note open on
               arrival, on top of the paragraph you were trying to read.
               The href stays real, so middle-click and no-JS still work. */
            back.addEventListener('click', function (e) {
                e.preventDefault();
                hidePopup();
                const marker = document.getElementById('noteref-' + n);
                if (marker) {
                    marker.scrollIntoView({ behavior: 'instant', block: 'center' });
                }
            });
            item.appendChild(back);

            list.appendChild(item);
        });
        aside.hidden = false;
    }

    function toMargin() {
        home.forEach(function (slot) {
            /* A note in the document always has a parent; ?. is for the checker
               and is a no-op at runtime. */
            slot.parent?.insertBefore(slot.node, slot.next);
        });
        list.innerHTML = '';
        aside.hidden = true;
    }

    /* ── Hover/focus preview ──────────────────────────────────────────────
       While a note sits at the foot of the page, hovering its marker shows it
       in place. CSS alone cannot do this: in end mode the note has been
       reparented into the endnotes list, so `.note-wrap:hover .note`
       matches nothing. One shared popup is borrowed by whichever marker is
       active, so the note is never duplicated in the DOM. */
    const popup = document.createElement('div');
    popup.className = 'note-popup';
    popup.hidden = true;
    document.body.appendChild(popup);

    let popupOwner: Borrowed | null = null;
    /* undefined rather than null: clearTimeout accepts undefined, so the reset
       needs no branch. */
    let hideTimer: number | undefined;

    function cancelHide() {
        window.clearTimeout(hideTimer);
        hideTimer = undefined;
    }

    function scheduleHide() {
        cancelHide();
        hideTimer = window.setTimeout(hidePopup, 150);
    }

    function hidePopup() {
        cancelHide();
        if (!popupOwner) {
            return;
        }
        /* Put the note back where it was before it was borrowed. */
        popupOwner.slot.parent?.insertBefore(popupOwner.note, popupOwner.slot.next);
        popupOwner = null;
        popup.hidden = true;
    }

    function showPopup(marker: HTMLElement, note: HTMLElement) {
        hidePopup();
        popupOwner = {
            note: note,
            slot: { parent: note.parentNode, next: note.nextSibling },
        };
        popup.appendChild(note);
        /* Park it off-screen before unhiding, so the measurement below happens
           somewhere harmless rather than at last show's coordinates. */
        popup.style.left = '-9999px';
        popup.style.top = '0';
        popup.hidden = false;

        /* Position under the marker, clamped into the viewport. */
        const m = marker.getBoundingClientRect();
        const p = popup.getBoundingClientRect();
        const margin = 12;
        let left = m.left + (m.width / 2) - (p.width / 2);
        left = Math.max(margin, Math.min(left, window.innerWidth - p.width - margin));
        let top = m.bottom + 6;
        if (top + p.height > window.innerHeight - margin) {
            top = Math.max(margin, m.top - p.height - 6);
        }
        popup.style.left = left + 'px';
        popup.style.top = top + 'px';
    }

    /* The popup is a hover target in its own right, so the pointer can travel
       from the marker onto it — note text may contain links, and an instant
       hide on mouseleave made those unclickable. */
    popup.addEventListener('mouseenter', cancelHide);
    popup.addEventListener('focusin', cancelHide);
    popup.addEventListener('mouseleave', scheduleHide);

    /* Keep this breakpoint in step with bp-note-float in assets/css/style.css. */
    const hasGutter = window.matchMedia('(min-width: 1361px)');

    let current: Mode | null = null;

    /* Margin wherever there is a gutter to float into, end wherever there
       is not. Nothing else feeds into it. */
    function effective(): Mode {
        return hasGutter.matches ? 'margin' : 'end';
    }

    function render(mode: Mode) {
        if (mode === current) {
            return;
        }
        /* A borrowed note must go home before anything else moves it. */
        hidePopup();
        /* Always restore first, so every transition starts from a known state. */
        if (current === 'end') {
            toMargin();
        }
        if (mode === 'end') {
            toEnd();
        }
        root.setAttribute('data-notes', mode);
        current = mode;
    }

    /* Re-evaluate whenever the gutter appears or disappears. */
    hasGutter.addEventListener('change', function () { render(effective()); });
    render(effective());
})();
